import styled, { css } from 'styled-components';
import { Label, Text } from '../../Typography/Text/Text';
import { FaBrandIconName, FaRegularIconName, FaSolidIconName, FaSolidIconNames, Icon, IconName } from '../Icons/Icon';
import React from 'react';
import { breakpoint } from '../../Themes/screensize';
import { Size } from '../../common/size';
import { useTestId } from '../../hooks/useTestId';
import { IThemePalette } from '../../Themes/interfaces';

export const optionButtonColours = (palette: IThemePalette) => ({
  optionButton: {
    label: {
      text: palette.primary.text[100],
      background: palette.neutral.gray[60],
      hover: {
        text: palette.primary.text[100],
        background: palette.primary.first[20]
      },
      focus: {
        text: palette.primary.text[100],
        background: palette.neutral.gray[60]
      },
      selected: {
        text: palette.primary.text[100],
        background: palette.primary.second[25]
      },
      selectedAndHover: {
        background: palette.primary.second[50]
      }
    },
    icon: {
      background: palette.alerts.success,
      border: palette.alerts.success
    }
  }
});

export const optionRadioButtonColours = (palette: IThemePalette) => ({
  optionRadioButton: {
    label: {
      text: palette.primary.text[100],
      background: palette.neutral.gray[60],
      hover: {
        text: palette.primary.text[100],
        background: palette.neutral.black[50]
      },
      focus: {
        text: palette.primary.text[100],
        background: palette.primary.first[50]
      },
      selected: {
        text: palette.primary.text[100],
        background: palette.primary.second[25]
      }
    },
    icon: {
      text: palette.neutral.white,
      border: palette.neutral.white,
      background: palette.neutral.white,
      hover: {
        text: palette.primary.text[100],
        border: palette.primary.first[75]
      }
    },
    iconContainer: {
      border: palette.primary.first[25],
      background: palette.neutral.white,
      hover: {
        text: palette.primary.text[100],
        border: palette.primary.first[75]
      }
    }
  }
});

interface IOptionButtonVariantProps {
  readOnly?: boolean;
  radio?: boolean;
  selected?: boolean;
}

interface IOptionButtonBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, IOptionButtonVariantProps {
  label?: string;
  renderLabel?: () => JSX.Element;
  body: string;
  icon?: IconName;
  farIcon?: FaRegularIconName;
  fasIcon?: FaSolidIconName;
  fabIcon?: FaBrandIconName;
}

interface IOptionButtonContainerProps extends IOptionButtonVariantProps {}

const baseCommonIconCss = css`
  border-radius: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  vertical-align: middle;
`;

const commonIconCss = css`
  ${baseCommonIconCss};
  visibility: hidden;
  background-color: ${({ theme }) => theme.colours.optionButton.icon.background};
  border: 1px solid ${({ theme }) => theme.colours.optionButton.icon.border};
`;

const OptionButtonIcon = styled(Icon)`
  ${commonIconCss}
  min-width: 1rem;
  min-height: 1rem;
`;

//fa-icon
const RadioIcon = styled(Icon)`
  ${baseCommonIconCss};
  background-color: ${({ theme }) => theme.colours.optionRadioButton.icon.background};
  color: ${({ theme }) => theme.colours.optionRadioButton.icon.text};
  && {
    width: 1rem;
  }
  height: 1rem;
  padding: 0;
`;

const RadioIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  height: 2rem;
  //required for both safari and firefox android to prevent expanding vertically
  max-height: 2rem;
  min-width: 2rem;
  border: 2px solid ${({ theme }) => theme.colours.optionRadioButton.iconContainer.border};
  background-color: ${({ theme }) => theme.colours.optionRadioButton.iconContainer.background};
  ${RadioIcon} {
    margin: 0 auto;
  }
`;

const commonOptionButtonCss = css`
  max-width: 32rem;
  width: 100%;
  min-height: 5.5rem;
  background-color: ${({ theme }) => theme.colours.optionButton.label.background};
  white-space: nowrap;
  margin: 0.5rem;
  border: none;
  border-radius: 0.25rem;
  padding: 1rem;
  gap: 1.5rem;
  display: flex;
  align-items: left;
  vertical-align: middle;
  box-sizing: border-box;
  @media (max-width: ${breakpoint.mobile}) {
    height: auto;
    white-space: normal;
  }
`;

const OptionButtonContainer = styled.button.attrs<IOptionButtonContainerProps>(() => ({
  type: 'button'
}))<IOptionButtonContainerProps>`
  ${commonOptionButtonCss}
  ${({ readOnly }) => !readOnly && `cursor: pointer`};
  ${({ selected, theme, readOnly }) =>
    selected &&
    css`
      background-color: ${!readOnly && theme.colours.optionButton.label.selected.background};
      ${OptionButtonIcon} {
        visibility: visible;
      }
      ${RadioIcon} {
        color: ${theme.colours.optionRadioButton.icon.hover.text};
      }
    `}
  :hover {
    background-color: ${({ theme, readOnly, selected }) =>
      selected ? !readOnly && theme.colours.optionButton.label.selectedAndHover.background : !readOnly && theme.colours.optionButton.label.hover.background};
  }
  :not(:focus):hover {
    @media (max-width: ${breakpoint.mobile}) {
      background-color: ${({ theme, selected }) =>
        selected ? theme.colours.optionButton.label.selected.background : theme.colours.optionButton.label.hover.background};
    }
  }
  ${(props) =>
    !props.radio &&
    css`
      justify-content: space-between;
    `}
`;

const StyledText = styled(Text)`
  margin: 0.5rem auto 0 0;
  white-space: normal;
`;

const OptionButtonBase: React.FC<IOptionButtonBaseProps> = ({
  icon,
  fasIcon,
  farIcon,
  fabIcon,
  label,
  body,
  renderLabel,
  radio,
  readOnly,
  selected,
  ...rest
}) => {
  const { generateTestId } = useTestId(rest, 'OptionButton');
  return (
    <OptionButtonContainer radio={radio} readOnly={readOnly} selected={selected} {...rest} {...generateTestId()}>
      {radio && (
        <RadioIconContainer>
          <RadioIcon fasName={FaSolidIconNames.faCircle} size={Size.Large} {...generateTestId('RadioIcon')} />
        </RadioIconContainer>
      )}
      <span {...generateTestId('span')}>
        {renderLabel && renderLabel()}
        {label && <Label {...generateTestId('Label')}>{label}</Label>}
        <StyledText size={Size.Medium} {...generateTestId('Text')}>
          {body}
        </StyledText>
      </span>
      {(icon || farIcon || fasIcon || fabIcon) && (
        <OptionButtonIcon name={icon} size={Size.Small} fabName={fabIcon} fasName={fasIcon} farName={farIcon} {...generateTestId('Icon')} />
      )}
    </OptionButtonContainer>
  );
};

export interface IOptionButtonProps extends Omit<IOptionButtonBaseProps, 'radio' | 'readOnly'> {}

export const OptionButton: React.FC<IOptionButtonProps> = ({ ...props }) => {
  const { generateTestId } = useTestId(props, 'OptionButton');
  return <OptionButtonBase {...props} {...generateTestId()} />;
};

export const OptionRadioButton: React.FC<IOptionButtonProps> = ({ ...props }) => {
  const { generateTestId } = useTestId(props, 'OptionRadioButton');
  return <OptionButtonBase radio {...props} {...generateTestId()} />;
};

export const OptionStatic: React.FC<IOptionButtonProps> = ({ ...props }) => {
  const { generateTestId } = useTestId(props, 'OptionStatic');
  return <OptionButtonBase readOnly {...props} {...generateTestId()} />;
};
